import clsx from 'clsx';
import type { ComponentType, FunctionComponent, ReactNode } from 'react';
import { useState } from 'react';

import {
    IconChevronDownSmall,
    IconChevronRightSmall,
    IconHumanInWindow,
    IconLearning,
    IconLevel,
    IconRole,
    IconSkills,
    IconTalentDirectory,
    IconTasks,
} from '~/ui/core/icons';
import { TableBody, TableContainer, TableFullWidthRow, TableGrid } from '~/ui/core/table/Table';

import { StatusIndicator } from '../../job-architecture-v2/components/StatusIndicator';
import { AuditDisplayShell } from '../../job-architecture-v2/job-profile-section/audit-table/AuditDisplayShell';
import {
    ACTION_BAR_AUDIT_COLUMN_WIDTHS,
    ACTION_BAR_LINE_WRAPPER_CLASS,
    ACTION_BAR_ROW_INSET_CLASS,
} from '../../job-architecture-v2/job-profile-section/audit-table/constants';

export type SkillReferenceItem = {
    readonly id: string;
    readonly onClick?: () => void;
    readonly title: string;
};

export type SkillReferenceKind =
    | 'competencies'
    | 'employees'
    | 'job-postings'
    | 'learning-opportunities'
    | 'levels'
    | 'roles'
    | 'tasks';

export type SkillReferenceRow = {
    readonly id: SkillReferenceKind;
    readonly items: readonly SkillReferenceItem[];
    readonly label: string;
    readonly totalCount: number;
};

type KindIcon = ComponentType<{ readonly size?: number }>;

const SkillsKindIcon: KindIcon = ({ size }) => <IconSkills height={size} width={size} />;

// Same glyphs the side navigation uses for each entity, so a kind reads the same everywhere.
const KIND_ICONS: Record<SkillReferenceKind, KindIcon> = {
    competencies: SkillsKindIcon,
    employees: IconTalentDirectory,
    'job-postings': IconHumanInWindow,
    'learning-opportunities': IconLearning,
    levels: IconLevel,
    roles: IconRole,
    tasks: IconTasks,
};

export const SKILL_REFERENCE_ITEMS_CAP = 20;

const STAMP_SIZE = 14;
const ROW_DIVIDER_CLASS = 'shadow-[inset_0_1px_0_var(--color-border-soft)]';
const KIND_CELL_CLASS = `!min-h-[34px] !items-center !justify-start !py-xs ${ACTION_BAR_ROW_INSET_CLASS}`;
// Same inset the audit trail's version-comment row uses: a 14px spacer aligns nested rows with the actor text.
const ITEM_CELL_CLASS = `!min-h-[34px] !items-center !justify-start !gap-xs !py-xs ${ACTION_BAR_ROW_INSET_CLASS}`;
const ITEM_TEXT_CLASS = 'min-w-0 flex-1 truncate text-xs font-medium tracking-tight text-text-primary';

const KindStamp: FunctionComponent<{
    readonly kind: SkillReferenceKind;
    readonly label: string;
    readonly muted: boolean;
}> = ({ kind, label, muted }) => {
    const Icon = KIND_ICONS[kind];

    return (
        <StatusIndicator
            className={muted ? 'text-text-disabled' : 'text-text-primary'}
            icon={
                <span
                    aria-hidden={true}
                    className={clsx(
                        'inline-flex shrink-0 items-center justify-center',
                        muted ? 'text-text-disabled' : 'text-text-content-subtle',
                    )}
                    style={{ height: STAMP_SIZE, width: STAMP_SIZE }}
                >
                    <Icon size={STAMP_SIZE} />
                </span>
            }
            size="xs"
            weight={muted ? 'medium' : 'semibold'}
        >
            {label}
        </StatusIndicator>
    );
};

const KindRow: FunctionComponent<{
    readonly isExpandable: boolean;
    readonly isExpanded: boolean;
    readonly isFirst: boolean;
    readonly listId: string;
    readonly onToggle: () => void;
    readonly row: SkillReferenceRow;
}> = ({ isExpandable, isExpanded, isFirst, listId, onToggle, row }) => {
    const muted = row.totalCount === 0;
    const content = (
        <span className={`${ACTION_BAR_LINE_WRAPPER_CLASS} justify-between gap-m`}>
            <KindStamp kind={row.id} label={row.label} muted={muted} />
            <span
                className={clsx(
                    'inline-flex min-h-[24px] shrink-0 items-center text-xs font-medium tracking-tight tabular-nums',
                    muted ? 'text-text-disabled' : 'text-text-primary',
                )}
            >
                <span className="min-w-[2ch] text-right">{row.totalCount}</span>
                <span aria-hidden={true} className="inline-flex w-[16px] shrink-0 items-center justify-center">
                    {isExpandable ? (
                        <IconChevronDownSmall
                            className={clsx(
                                'text-text-content-faint transition-transform duration-200',
                                isExpanded && 'rotate-180',
                            )}
                            size={16}
                        />
                    ) : null}
                </span>
            </span>
        </span>
    );

    return (
        <TableFullWidthRow
            $cellSize="small"
            $columnWidths={ACTION_BAR_AUDIT_COLUMN_WIDTHS}
            $size="small"
            cellClassName={KIND_CELL_CLASS}
            className={clsx('!mt-0 !rounded-none', isFirst ? '!bg-transparent' : ROW_DIVIDER_CLASS, {
                '!bg-surface-raised': isExpanded,
                '!bg-transparent': !isExpanded,
            })}
        >
            {isExpandable ? (
                <button
                    aria-controls={listId}
                    aria-expanded={isExpanded}
                    className="m-0 flex w-full min-w-0 cursor-pointer items-center border-none bg-transparent p-0 text-left"
                    type="button"
                    onClick={onToggle}
                >
                    {content}
                </button>
            ) : (
                content
            )}
        </TableFullWidthRow>
    );
};

const ItemRow: FunctionComponent<{ readonly children: ReactNode }> = ({ children }) => (
    <TableFullWidthRow
        $cellSize="small"
        $columnWidths={ACTION_BAR_AUDIT_COLUMN_WIDTHS}
        $size="small"
        cellClassName={ITEM_CELL_CLASS}
        className={`!mt-0 !rounded-none !bg-transparent ${ROW_DIVIDER_CLASS}`}
    >
        <span aria-hidden={true} className="shrink-0 basis-[14px]" />
        {children}
    </TableFullWidthRow>
);

const ReferenceGroup: FunctionComponent<{
    readonly isExpanded: boolean;
    readonly isFirst: boolean;
    readonly onToggle: () => void;
    readonly row: SkillReferenceRow;
}> = ({ isExpanded, isFirst, onToggle, row }) => {
    const visibleItems = row.items.slice(0, SKILL_REFERENCE_ITEMS_CAP);
    const isExpandable = visibleItems.length > 0;
    const listId = `skill-reference-${row.id}`;

    return (
        <div className="contents" data-testid={`skill-reference-row-${row.id}`}>
            <KindRow
                isExpandable={isExpandable}
                isExpanded={isExpanded}
                isFirst={isFirst}
                listId={listId}
                row={row}
                onToggle={onToggle}
            />
            {isExpandable && isExpanded ? (
                <div className="contents" id={listId}>
                    {visibleItems.map((item) => (
                        <ItemRow key={item.id}>
                            {item.onClick === undefined ? (
                                <span className={`${ACTION_BAR_LINE_WRAPPER_CLASS} ${ITEM_TEXT_CLASS}`}>
                                    {item.title}
                                </span>
                            ) : (
                                <button
                                    className={`${ACTION_BAR_LINE_WRAPPER_CLASS} m-0 cursor-pointer gap-m border-none bg-transparent p-0 text-left hover:text-text-secondary`}
                                    type="button"
                                    onClick={item.onClick}
                                >
                                    <span className={ITEM_TEXT_CLASS}>{item.title}</span>
                                    <IconChevronRightSmall className="shrink-0 text-text-content-faint" size={16} />
                                </button>
                            )}
                        </ItemRow>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

export const SkillReferencesList: FunctionComponent<{
    readonly rows: readonly SkillReferenceRow[];
    readonly testId?: string;
    readonly title: string;
}> = ({ rows, testId, title }) => {
    const [expandedRowIds, setExpandedRowIds] = useState<ReadonlySet<string>>(new Set());

    const toggleRow = (rowId: string): void => {
        setExpandedRowIds((current) => {
            const next = new Set(current);

            if (next.has(rowId)) {
                next.delete(rowId);
            } else {
                next.add(rowId);
            }

            return next;
        });
    };

    return (
        <section
            className="flex flex-col gap-xxs overflow-hidden rounded-xl bg-surface-hover p-xxs shadow-sm"
            data-testid={testId}
        >
            <h3
                className={`m-0 flex items-center py-xs text-body-s font-medium tracking-tight text-text-primary ${ACTION_BAR_ROW_INSET_CLASS}`}
            >
                {title}
            </h3>
            <AuditDisplayShell footerAction={null}>
                <TableContainer $inset="none" className="!h-auto !rounded-none !bg-transparent">
                    <TableGrid
                        $horizontalScroll={false}
                        aria-label={title}
                        className="!rounded-none !overflow-visible"
                        role="grid"
                    >
                        <TableBody className="!overflow-visible">
                            {rows.map((row, index) => (
                                <ReferenceGroup
                                    key={row.id}
                                    isExpanded={expandedRowIds.has(row.id)}
                                    isFirst={index === 0}
                                    row={row}
                                    onToggle={() => toggleRow(row.id)}
                                />
                            ))}
                        </TableBody>
                    </TableGrid>
                </TableContainer>
            </AuditDisplayShell>
        </section>
    );
};
